/**
 * Study Plan Types
 * Type definitions for the AI-powered study plan generation system
 */

// ============================================================================
// STUDY PLAN ENUMS
// ============================================================================

export type StudyPlanStatus = 'active' | 'completed' | 'archived' | 'paused';

export type StudyPlanType =
  | 'general'     // Ongoing study
  | 'exam_prep'   // Preparing for specific exam
  | 'bar_prep'    // Bar exam preparation
  | 'finals';     // Finals week intensive

export type ActivityType =
  | 'flashcards'    // Flashcard review session
  | 'practice'      // Practice questions/quizzes
  | 'chat'          // AI chat for conceptual help
  | 'essay'         // Essay practice
  | 'issue_spotter' // Issue spotting exercise
  | 'video'         // Course video
  | 'reading'       // Blog/outline reading
  | 'review'        // General review
  | 'mee'           // MEE (Multistate Essay Exam) practice
  | 'mpt';          // MPT (Multistate Performance Test) practice

// ============================================================================
// PLAN STRUCTURE TYPES
// ============================================================================

/** Individual study activity within a day */
export interface StudyActivity {
  id: string;
  type: ActivityType;
  title: string;
  description?: string;
  estimated_minutes: number;
  subject: string;
  topic?: string;
  resource_url?: string;
  priority: number; // 1-3
  is_completed: boolean;
  completed_at?: string;
}

/** Single day's study schedule */
export interface StudyDay {
  date: string; // ISO date string
  day_of_week: string;
  activities: StudyActivity[];
  total_minutes: number;
  completed_minutes: number;
  is_rest_day: boolean;
}

/** Week of study activities */
export interface StudyWeek {
  week_number: number;
  start_date: string;
  end_date: string;
  focus_topics: string[];
  days: StudyDay[];
  milestone?: string;
}

/** Full plan data structure stored in plan_data JSONB */
export interface StudyPlanData {
  weeks: StudyWeek[];
  milestones: {
    date: string;
    title: string;
    description?: string;
    is_achieved: boolean;
  }[];
  focus_areas: {
    subject: string;
    topics: string[];
    priority: 'high' | 'medium' | 'low';
    reason: string;
  }[];
  daily_target_minutes: number;
  recommended_activities: {
    type: ActivityType;
    frequency: string; // e.g., "3x/week"
    reason: string;
  }[];

  /** Phased plan data (optional - only for phased plans) */
  phases?: StudyPhase[];

  /** Subject classifications with tier and priority (optional - only for phased plans) */
  subject_classifications?: SubjectClassification[];

  /** Time availability configuration used to generate this plan */
  time_availability?: TimeAvailability;
}

/** Metadata about how the plan was generated */
export interface GenerationMetadata {
  model?: string;
  input_hash: string;
  generated_at: string;
  diagnostic_ids?: string[];
  syllabus_ids?: string[];
  profile_version?: number;
  confidence_score?: number;
}

// ============================================================================
// STUDY PLAN INTERFACE
// ============================================================================

export interface StudyPlan {
  id: string;
  user_id: string;

  // Basic info
  title: string;
  description: string | null;
  exam_date: string | null;
  start_date: string;
  end_date: string | null;

  // Configuration
  subjects: string[];
  status: StudyPlanStatus;
  plan_type: StudyPlanType;

  // Plan content
  plan_data: StudyPlanData | null;
  generation_metadata: GenerationMetadata | null;

  // Progress tracking
  total_tasks: number;
  completed_tasks: number;
  progress_percentage: number;

  // Timestamps
  created_at: string | null;
  updated_at: string | null;
}

// ============================================================================
// PLAN GENERATION TYPES
// ============================================================================

export interface GeneratePlanRequest {
  /** Subjects to include in plan */
  subjects: string[];

  /** Target exam date (optional) */
  exam_date?: string;

  /** Plan type */
  plan_type?: StudyPlanType;

  /** Syllabus IDs to incorporate */
  syllabus_ids?: string[];

  /** Daily study time target in minutes (deprecated - use time_availability) */
  daily_minutes?: number;

  /** Specific topics to focus on */
  focus_topics?: string[];

  /** Days of week available (0=Sun, 6=Sat) (deprecated - use time_availability) */
  available_days?: number[];

  /** Detailed time availability with weekday/weekend differentiation */
  time_availability?: TimeAvailability;

  /** Whether to generate a phased plan (Rebuild → Stabilization → Integration → Conversion) */
  generate_phased?: boolean;

  /** External score import ID for subject prioritization */
  score_import_id?: string;
}

export interface GeneratePlanResponse {
  success: boolean;
  plan_id?: string;
  plan?: StudyPlan;
  error?: string;
}

export interface PlanGenerationEvent {
  type: 'progress' | 'stage' | 'result' | 'error' | 'complete';
  stage?: 'analyzing' | 'scoring' | 'scheduling' | 'creating_tasks';
  progress?: number;
  message?: string;
  plan?: StudyPlan;
  error?: string;
}

// ============================================================================
// PLAN UPDATE TYPES
// ============================================================================

export interface UpdatePlanRequest {
  plan_id: string;
  status?: StudyPlanStatus;
  title?: string;
  exam_date?: string;
  daily_minutes?: number;
}

// ============================================================================
// DISPLAY HELPERS
// ============================================================================

export const PLAN_STATUS_LABELS: Record<StudyPlanStatus, string> = {
  active: 'Active',
  completed: 'Completed',
  archived: 'Archived',
  paused: 'Paused',
} as const;

export const PLAN_STATUS_COLORS: Record<StudyPlanStatus, { bg: string; text: string }> = {
  active: {
    bg: 'bg-green-100 dark:bg-green-900/30',
    text: 'text-green-600 dark:text-green-400',
  },
  completed: {
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    text: 'text-blue-600 dark:text-blue-400',
  },
  archived: {
    bg: 'bg-gray-100 dark:bg-gray-800',
    text: 'text-gray-600 dark:text-gray-400',
  },
  paused: {
    bg: 'bg-amber-100 dark:bg-amber-900/30',
    text: 'text-amber-600 dark:text-amber-400',
  },
} as const;

export const PLAN_TYPE_LABELS: Record<StudyPlanType, string> = {
  general: 'General Study',
  exam_prep: 'Exam Prep',
  bar_prep: 'Bar Prep',
  finals: 'Finals Prep',
} as const;

export const ACTIVITY_TYPE_LABELS: Record<ActivityType, string> = {
  flashcards: 'Flashcard Review',
  practice: 'Practice Questions',
  chat: 'AI Chat Session',
  essay: 'Essay Practice',
  issue_spotter: 'Issue Spotting',
  video: 'Video Lesson',
  reading: 'Reading',
  review: 'Review Session',
  mee: 'MEE Practice',
  mpt: 'MPT Practice',
} as const;

export const ACTIVITY_TYPE_ICONS: Record<ActivityType, string> = {
  flashcards: 'GraduationCap',
  practice: 'ClipboardList',
  chat: 'MessageSquare',
  essay: 'FileText',
  issue_spotter: 'Search',
  video: 'Play',
  reading: 'BookOpen',
  review: 'RefreshCw',
  mee: 'FileText',
  mpt: 'FileText',
} as const;

// ============================================================================
// CONFIGURATION
// ============================================================================

/** Default daily study time in minutes */
export const DEFAULT_DAILY_MINUTES = 60;

/** Maximum plan duration in weeks */
export const MAX_PLAN_WEEKS = 16;

/** Default activities per day */
export const DEFAULT_ACTIVITIES_PER_DAY = 3;

// ============================================================================
// PHASED PLAN TYPES (Enhanced Study Plan Generation)
// ============================================================================

/**
 * Time availability configuration for study planning
 * Stored in learning_profiles.time_availability
 */
export interface TimeAvailability {
  /** Minutes available for study on weekdays (Mon-Fri) */
  weekday_minutes: number;
  /** Minutes available for study on weekend days */
  weekend_minutes: number;
  /** Weekend study pattern */
  weekend_pattern: 'every' | 'every_other' | 'none';
  /** Days available for study (0=Sun through 6=Sat) */
  preferred_days: number[];
  /** Allow longer intensive weekend sessions */
  intensive_weekends: boolean;
}

/** Default time availability for new users */
export const DEFAULT_TIME_AVAILABILITY: TimeAvailability = {
  weekday_minutes: 120,    // 2 hours
  weekend_minutes: 360,    // 6 hours
  weekend_pattern: 'every',
  preferred_days: [0, 1, 2, 3, 4, 5, 6], // All days
  intensive_weekends: false,
};

/**
 * Subject tier classification based on performance data
 * Used to prioritize subjects in phased planning
 */
export type SubjectTier = 'critical' | 'weak' | 'below_average' | 'average' | 'strong';

/** Subject tier thresholds (percentile-based) */
export const SUBJECT_TIER_THRESHOLDS: Record<SubjectTier, { min: number; max: number }> = {
  critical: { min: 0, max: 25 },
  weak: { min: 25, max: 40 },
  below_average: { min: 40, max: 50 },
  average: { min: 50, max: 70 },
  strong: { min: 70, max: 100 },
};

/** Display labels for subject tiers */
export const SUBJECT_TIER_LABELS: Record<SubjectTier, string> = {
  critical: 'Critical - Needs Rebuild',
  weak: 'Weak - Needs Focus',
  below_average: 'Below Average - Room for Growth',
  average: 'Average - Maintain',
  strong: 'Strong - Light Maintenance',
};

/** Colors for subject tiers */
export const SUBJECT_TIER_COLORS: Record<SubjectTier, { bg: string; text: string; border: string }> = {
  critical: {
    bg: 'bg-red-100 dark:bg-red-900/30',
    text: 'text-red-700 dark:text-red-400',
    border: 'border-red-300 dark:border-red-700',
  },
  weak: {
    bg: 'bg-orange-100 dark:bg-orange-900/30',
    text: 'text-orange-700 dark:text-orange-400',
    border: 'border-orange-300 dark:border-orange-700',
  },
  below_average: {
    bg: 'bg-amber-100 dark:bg-amber-900/30',
    text: 'text-amber-700 dark:text-amber-400',
    border: 'border-amber-300 dark:border-amber-700',
  },
  average: {
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    text: 'text-blue-700 dark:text-blue-400',
    border: 'border-blue-300 dark:border-blue-700',
  },
  strong: {
    bg: 'bg-green-100 dark:bg-green-900/30',
    text: 'text-green-700 dark:text-green-400',
    border: 'border-green-300 dark:border-green-700',
  },
};

/**
 * Classification of a subject with its tier and allocation
 */
export interface SubjectClassification {
  /** Subject identifier */
  subject: string;
  /** Display name */
  display_name: string;
  /** Urgency tier */
  tier: SubjectTier;
  /** Percentile from external score import (if available) */
  percentile?: number;
  /** Mastery score from diagnostics/practice (0-1) */
  mastery_score?: number;
  /** Time allocation percentage within phase */
  allocation_percentage: number;
  /** Priority score (0-100, higher = more urgent) */
  priority_score: number;
  /** Reason for classification */
  reason: string;
}

/**
 * Phase name for phased study plans
 */
export type PhaseName = 'rebuild' | 'stabilization' | 'integration' | 'conversion';

/** Phase display labels */
export const PHASE_LABELS: Record<PhaseName, string> = {
  rebuild: 'Full Rebuild',
  stabilization: 'Stabilization',
  integration: 'Integration',
  conversion: 'Exam Conversion',
};

/** Phase descriptions for UI */
export const PHASE_DESCRIPTIONS: Record<PhaseName, string> = {
  rebuild: 'Focus exclusively on your weakest subjects with foundational practice.',
  stabilization: 'Strengthen borderline subjects while maintaining gains in rebuilt areas.',
  integration: 'Fully mixed, timed practice sets to build exam stamina.',
  conversion: 'Final exam mode - no new material, focus on confidence and execution.',
};

/**
 * Activity mix percentages for a phase
 */
export interface ActivityMix {
  /** MBE practice percentage */
  mbe_percentage: number;
  /** Written component (MEE/MPT) percentage */
  written_percentage: number;
  /** Review/flashcard percentage */
  review_percentage: number;
}

/**
 * Exit standard metric type
 */
export type ExitStandardMetric = 'accuracy' | 'timing' | 'completion';

/**
 * Exit standard definition - criteria to move to next phase
 */
export interface ExitStandard {
  /** Metric being measured */
  metric: ExitStandardMetric;
  /** Threshold to achieve (e.g., 60 for 60% accuracy) */
  threshold: number;
  /** Number of consecutive sets required */
  consecutive_sets: number;
  /** Subject this applies to (null = all subjects in phase) */
  subject?: string | null;
  /** Human-readable description */
  description: string;
}

/**
 * Progress toward an exit standard (from database)
 */
export interface ExitStandardProgress {
  id: string;
  user_id: string;
  study_plan_id: string;
  phase_name: PhaseName;
  subject: string | null;
  metric: ExitStandardMetric;
  threshold: number;
  consecutive_required: number;
  consecutive_achieved: number;
  best_score: number | null;
  attempts: number;
  achieved_at: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * A single phase in a phased study plan
 */
export interface StudyPhase {
  /** Phase name */
  name: PhaseName;
  /** Display label */
  label: string;
  /** Phase description */
  description: string;
  /** Starting week number (1-indexed) */
  week_start: number;
  /** Ending week number (1-indexed) */
  week_end: number;
  /** Total weeks in this phase */
  total_weeks: number;
  /** Subject allocations for this phase */
  subject_allocations: SubjectClassification[];
  /** Activity mix for this phase */
  activity_mix: ActivityMix;
  /** Exit standards to complete this phase */
  exit_standards: ExitStandard[];
  /** Focus mode for activities */
  focus: 'single_subject' | 'multi_subject' | 'mixed';
  /** Whether this is the current active phase */
  is_current: boolean;
  /** Whether this phase is completed */
  is_completed: boolean;
  /** User-facing summary (2nd person language) */
  summary: string;
  /** Key study instructions (2nd person language) */
  instructions: string[];
}

/**
 * Weekly structure for weekday vs weekend
 */
export interface WeekStructure {
  /** Weekday study blocks */
  weekday: StudyBlock[];
  /** Weekend Day 1 blocks (MBE Heavy) */
  weekend_day1: StudyBlock[];
  /** Weekend Day 2 blocks (Written Focus) */
  weekend_day2: StudyBlock[];
}

/**
 * A study block within a day
 */
export interface StudyBlock {
  /** Block name (e.g., "MBE Practice") */
  name: string;
  /** Duration in minutes */
  duration_minutes: number;
  /** Activity type */
  activity_type: ActivityType;
  /** Description of what to do */
  description: string;
  /** Subject focus (null = mixed) */
  subject?: string | null;
}

/**
 * Written component schedule
 */
export interface WrittenComponent {
  /** MEEs per week */
  mees_per_week: number;
  /** MPTs per week */
  mpts_per_week: number;
  /** Focus on which subjects */
  focus_subjects: string[];
  /** General instructions */
  instructions: string[];
}

/**
 * Extended study plan data for phased plans
 */
export interface PhasedStudyPlanData extends StudyPlanData {
  /** Study phases */
  phases: StudyPhase[];
  /** Subject classifications */
  subject_classifications: SubjectClassification[];
  /** Time availability used to generate plan */
  time_availability: TimeAvailability;
  /** Week structure template */
  week_structure: WeekStructure;
  /** Written component configuration */
  written_component: WrittenComponent;
  /** Total plan duration in weeks */
  total_weeks: number;
  /** Current phase index (0-3) */
  current_phase_index: number;
  /** Whether this is a phased plan (vs basic rotation) */
  is_phased: boolean;
}

/**
 * Extended generation request for phased plans
 */
export interface GeneratePhasedPlanRequest extends GeneratePlanRequest {
  /** Time availability override (uses profile default if not provided) */
  time_availability?: TimeAvailability;
  /** External score import ID to use for prioritization */
  score_import_id?: string;
  /** Whether to generate phased plan (default true for bar_prep) */
  generate_phased?: boolean;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Classify a subject tier based on percentile
 */
export function classifySubjectTier(percentile: number): SubjectTier {
  if (percentile < 25) return 'critical';
  if (percentile < 40) return 'weak';
  if (percentile < 50) return 'below_average';
  if (percentile < 70) return 'average';
  return 'strong';
}

/**
 * Calculate phase allocation percentages based on total weeks
 */
export function calculatePhaseWeeks(totalWeeks: number): Record<PhaseName, number> {
  // Default allocations: ~35% rebuild, ~30% stabilization, ~20% integration, ~15% conversion
  const rebuild = Math.max(2, Math.round(totalWeeks * 0.35));
  const stabilization = Math.max(2, Math.round(totalWeeks * 0.30));
  const conversion = Math.max(1, Math.round(totalWeeks * 0.15));
  const integration = Math.max(1, totalWeeks - rebuild - stabilization - conversion);

  return {
    rebuild,
    stabilization,
    integration,
    conversion,
  };
}

/**
 * Format time availability for display
 */
export function formatTimeAvailability(ta: TimeAvailability): string {
  const weekday = ta.weekday_minutes >= 60
    ? `${Math.round(ta.weekday_minutes / 60)}h`
    : `${ta.weekday_minutes}min`;
  const weekend = ta.weekend_minutes >= 60
    ? `${Math.round(ta.weekend_minutes / 60)}h`
    : `${ta.weekend_minutes}min`;

  const weekendPattern = ta.weekend_pattern === 'every_other'
    ? 'every other weekend'
    : ta.weekend_pattern === 'none'
    ? 'no weekends'
    : 'weekends';

  return `${weekday}/day weekdays, ${weekend}/day ${weekendPattern}`;
}
